import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import { formatDate } from "@/utils";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type {
  Education,
  EducationFormat,
  EducationStatus,
} from "@/features/education-management/types/education-management.types";
import type {
  UpcomingTraining,
  UpcomingTrainingStatus,
  DashboardAnnouncement,
  DashboardUpcomingTrainingsParams,
  DashboardAnnouncementsParams,
  PaginatedResponse,
  DashboardSummaryApiResponse,
  DashboardSummary,
  DashboardEducationStatus,
  DashboardCategorySuccessCompletion,
  EducationMapResponse,
  ProvinceEducationDetailResponse,
} from "../types/dashboard.types";

/** Backend eğitim formatı (EducationFormat) → tablo etiketi */
const UPCOMING_FORMAT_LABEL: Record<EducationFormat, string> = {
  yuzYuze: "Yüz Yüze",
  canli: "Canlı",
  uniteli: "Üniteli",
  nonUnit: "Ünitesiz",
};

/** Backend eğitim durumu (EducationStatus) → dashboard chip durumu */
const UPCOMING_STATUS_MAP: Record<EducationStatus, UpcomingTrainingStatus> = {
  talepToplama: "request_collection",
  butceBekliyor: "budget_pending",
  aktif: "active",
  kapali: "closed",
  tamamlandi: "closed",
  taslak: "approval_pending",
};

function mapEducationToUpcoming(e: Education): UpcomingTraining {
  return {
    id: e.id,
    trainingName: e.educationName,
    format: UPCOMING_FORMAT_LABEL[e.format] ?? e.format,
    registrationCount: e.registrationCount ?? 0,
    maxRegistrations:
      e.maxRegistration && e.maxRegistration > 0 ? e.maxRegistration : null,
    publicationDate: e.publishDate ? formatDate(e.publishDate, "DD.MM.YYYY") : "-",
    status: UPCOMING_STATUS_MAP[e.status] ?? "active",
  };
}

const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const MOCK_ANNOUNCEMENTS: DashboardAnnouncement[] = [
  {
    id: "1",
    imageUrl: "https://picsum.photos/seed/d1/80/80",
    title: i18n.t("mockData.announcement.systemMaintenance"),
    time: "13:45",
    tag: i18n.t("mockData.tags.systemMaintenance"),
    tagType: "system",
  },
  {
    id: "2",
    imageUrl: "https://picsum.photos/seed/d2/80/80",
    title: i18n.t("mockData.announcement.osgReminder"),
    time: "21:32",
    tag: i18n.t("mockData.tags.reminder"),
    tagType: "reminder",
  },
  {
    id: "3",
    imageUrl: "https://picsum.photos/seed/d3/80/80",
    title: i18n.t("mockData.announcement.libraryCatalog"),
    time: "12.02.2026",
    tag: "Yeni Katalog",
    tagType: "catalog",
  },
  {
    id: "4",
    imageUrl: "https://picsum.photos/seed/d4/80/80",
    title: i18n.t("mockData.announcement.yearlyPlan"),
    time: "11.02.2026",
    tag: "Eğitim",
    tagType: "education",
  },
  {
    id: "5",
    imageUrl: "https://picsum.photos/seed/d5/80/80",
    title: i18n.t("mockData.announcement.keypUpdate"),
    time: "10.02.2026",
    tag: i18n.t("mockData.tags.systemMaintenance"),
    tagType: "system",
  },
  {
    id: "6",
    imageUrl: "https://picsum.photos/seed/d6/80/80",
    title: i18n.t("mockData.announcement.certRenewal"),
    time: "09.02.2026",
    tag: i18n.t("mockData.tags.reminder"),
    tagType: "reminder",
  },
];

async function getDashboardSummary(params?: {
  /** Chart verilerini son N gün ile sınırla (BE desteği beklendiğinde aktifleşir). */
  periodDays?: number;
}): Promise<{
  summary: DashboardSummary;
  educationStatus: DashboardEducationStatus;
  categorySuccessCompletion: DashboardCategorySuccessCompletion;
}> {
  const { data } = await apiClient.get<DashboardSummaryApiResponse>(
    "/api/admin/dashboard",
    {
      params: params?.periodDays
        ? { periodDays: params.periodDays }
        : undefined,
    },
  );

  if (!data?.data) {
    throw new Error("Dashboard summary not found");
  }

  return data.data;
}

const unwrapApiData = <T>(response: unknown): T => {
  const value = response as { data?: unknown };
  if (value?.data && typeof value.data === "object" && "data" in value.data) {
    return (value.data as { data: T }).data;
  }
  if (value?.data !== undefined) return value.data as T;
  return response as T;
};


async function getEducationMap(params?: {
  endDate?: string;
  period?: string;
  startDate?: string;
}): Promise<EducationMapResponse> {
  const response = await apiClient.get("/api/dashboard/education-map", {
    params,
    skipErrorToast: true,
  });

  return unwrapApiData<EducationMapResponse>(response.data);
}

async function getEducationMapProvince(
  provinceId: string,
  params?: {
    endDate?: string;
    page?: number;
    pageSize?: number;
    period?: string;
    startDate?: string;
  },
): Promise<ProvinceEducationDetailResponse> {
  const response = await apiClient.get(
    `/api/dashboard/education-map/provinces/${provinceId}`,
    {
      params,
      skipErrorToast: true,
    },
  );

  return unwrapApiData<ProvinceEducationDetailResponse>(response.data);
}


export const dashboardApi = {
  /**
   * GET /api/admin/dashboard - top summary + charts for admin dashboard.
   */
  getDashboardSummary,
  /**
   * Get upcoming trainings with pagination (for dashboard table)
   */
  getUpcomingTrainings: async (
    params?: DashboardUpcomingTrainingsParams
  ): Promise<PaginatedResponse<UpcomingTraining>> => {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    // "Yaklaşan Eğitimler": bugünden itibaren başlayan eğitimler, yayın
    // tarihine göre artan sırada. Gerçek backend (GET /api/admin/educations).
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const res = await educationManagementApi.getAll({
      page,
      pageSize,
      sortBy: "publishedAt",
      sortOrder: "asc",
      filters: { publishDate: [today, null] },
    });
    return {
      data: res.data.map(mapEducationToUpcoming),
      total: res.total,
      page,
      pageSize,
    };
  },

  /**
   * Get announcements for dashboard list (general/private tab, paginated)
   */
  getAnnouncements: async (
    params?: DashboardAnnouncementsParams
  ): Promise<PaginatedResponse<DashboardAnnouncement>> => {
    await delay(300);
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 5;
    const tab = params?.tab ?? "general";
    let list = MOCK_ANNOUNCEMENTS;
    if (tab === "private") {
      list = MOCK_ANNOUNCEMENTS.filter((a) => a.tagType === "reminder");
    }
    const start = (page - 1) * pageSize;
    const data = list.slice(start, start + pageSize);
    return {
      data,
      total: list.length,
      page,
      pageSize,
    };
  },
  /**
   * GET /api/dashboard/education-map - regional education map summary.
   */
  getEducationMap,
  /**
   * GET /api/dashboard/education-map/provinces/:provinceId - selected province trainings.
   */
  getEducationMapProvince,
};
